/**
 * Helper functions for building chat view state and handlers.
 */

import type { ConversationResponse } from '@saaga/shared/interfaces/conversation.interface'

import type { ChatPinnedMessage } from '@/components/chat/chat-header.interface'
import type { ChatAttachment } from '@/components/chat/chat-input-attachment.interface'
import type { ChatInputMode } from '@/components/chat/chat-input-mode.type'
import type {
  ChatItem,
  ChatParticipant,
  ChatUserInteraction,
} from '@/components/chat/chat-message.interface'
import type { PromptTemplate } from '@/components/chat/prompt-template.interface'
import type { UseChatVirtualListReturn } from '@/components/chat/use-chat-virtual-list.hook'
import type { ParsedTemplateVariable } from '@/components/organisms/template-variable-editor/template-variable.interface'

import type { UseChatHeaderActionsReturn } from './use-chat-header-actions.hook'
import type { UseChatInputReturn } from './use-chat-input.hook'
import type { UseChatSplitViewReturn } from './use-chat-split-view.hook'

export interface ChatViewState {
  itemList: ChatItem[]
  participantList: ChatParticipant[]
  pinnedMessageList: ChatPinnedMessage[]
  highlightedMessageId: string | null
  jumpingPinnedMessageId: string | null
  virtualListBinding: UseChatVirtualListReturn
  inputValue: string
  inputMode: ChatInputMode
  attachmentList: ChatAttachment[]
  templateList: PromptTemplate[]
  selectedTemplate: PromptTemplate | null
  isTemplateFillOpen: boolean
  templateFillVariableList: ParsedTemplateVariable[]
  isLoading: boolean
  isSending: boolean
  isStreaming: boolean
  isStopping: boolean
  hasMoreMessages: boolean
  isFetchingMoreMessages: boolean
  loadMoreMessages: () => void
  conversationId: string | null
  conversation: ConversationResponse | undefined
  isNewConversation: boolean
  currentUserId: string
  isBalanceDepleted: boolean
  isPlanRestricted: boolean
  userInteractionCardList: ChatUserInteraction[]
  isFileAttachEnabled: boolean
  isUploading: boolean
  uploadProgress: number
  agencySlug: string | null
}

export interface ChatViewHandlers
  extends Omit<UseChatHeaderActionsReturn, 'isTitleEditing' | 'isTitleSaving'>,
    Pick<
      UseChatInputReturn,
      | 'setInputValue'
      | 'setInputMode'
      | 'handleRemoveAttachment'
      | 'handleFilesSelected'
      | 'handleAttachFromChats'
      | 'handleTemplateSelect'
      | 'handleCreateTemplate'
      | 'handleTemplateFillSubmit'
      | 'handleTemplateFillCancel'
    >,
    Pick<
      UseChatSplitViewReturn,
      | 'handleToolCallClick'
      | 'handleContentBriefClick'
      | 'handleMessageClick'
      | 'handleReportClick'
      | 'handleUserInteractionClick'
      | 'handleCloseSplitView'
    > {
  handleSend: () => void
  handleStop: () => void
  handleAttachmentDownload: (attachmentId: string) => void
}

export interface ChatViewTitleEditState {
  isTitleEditing: boolean
  isTitleSaving: boolean
}

interface BuildStateParams {
  messages: {
    itemList: ChatItem[]
    isLoading: boolean
    isSending: boolean
    hasMoreMessages: boolean
    isFetchingMoreMessages: boolean
    loadMoreMessages: () => void
  }
  participantList: ChatParticipant[]
  pinnedMessageList: ChatPinnedMessage[]
  highlightedMessageId: string | null
  jumpingPinnedMessageId: string | null
  virtualListBinding: UseChatVirtualListReturn
  input: UseChatInputReturn
  conversationId: string | null
  conversation: ConversationResponse | undefined
  isNewConversation: boolean
  currentUserId: string
  isBalanceDepleted: boolean
  isPlanRestricted: boolean
  userInteractionCardList: ChatUserInteraction[]
  isStreaming: boolean
  isStopping: boolean
  isUploading: boolean
  uploadProgress: number
  agencySlug: string | null
}

const PLAN_RESTRICTED_CARD: ChatUserInteraction = {
  id: 'deep-link-plan-restricted',
  type: 'user-interaction',
  interactionType: 'upgrade-plan',
  title: 'Upgrade Your Plan',
  timestamp: new Date().toISOString(),
}

const buildItemList = (params: BuildStateParams): ChatItem[] => {
  if (params.isPlanRestricted) {
    return [PLAN_RESTRICTED_CARD]
  }
  return params.messages.itemList
}

export const buildChatViewState = (params: BuildStateParams): ChatViewState => ({
  itemList: buildItemList(params),
  participantList: params.participantList,
  pinnedMessageList: params.pinnedMessageList,
  highlightedMessageId: params.highlightedMessageId,
  jumpingPinnedMessageId: params.jumpingPinnedMessageId,
  virtualListBinding: params.virtualListBinding,
  inputValue: params.input.inputValue,
  inputMode: params.input.inputMode,
  attachmentList: params.input.attachmentList,
  templateList: params.input.templateList,
  selectedTemplate: params.input.selectedTemplate,
  isTemplateFillOpen: params.input.isTemplateFillOpen,
  templateFillVariableList: params.input.templateFillVariableList,
  isLoading: params.messages.isLoading,
  isSending: params.messages.isSending,
  isStreaming: params.isStreaming,
  isStopping: params.isStopping,
  hasMoreMessages: params.messages.hasMoreMessages,
  isFetchingMoreMessages: params.messages.isFetchingMoreMessages,
  loadMoreMessages: params.messages.loadMoreMessages,
  conversationId: params.conversationId,
  conversation: params.conversation,
  isNewConversation: params.isNewConversation,
  currentUserId: params.currentUserId,
  isBalanceDepleted: params.isBalanceDepleted,
  isPlanRestricted: params.isPlanRestricted,
  userInteractionCardList: params.userInteractionCardList,
  isFileAttachEnabled: params.input.isFileAttachEnabled,
  isUploading: params.isUploading,
  uploadProgress: params.uploadProgress,
  agencySlug: params.agencySlug,
})

interface BuildHandlersParams {
  input: UseChatInputReturn
  splitViewActions: UseChatSplitViewReturn
  headerActions: UseChatHeaderActionsReturn
  handleSend: () => void
  handleStop: () => void
  handleAttachmentDownload: (attachmentId: string) => void
}

export const buildChatViewHandlers = (params: BuildHandlersParams): ChatViewHandlers => {
  const { isTitleEditing: _, isTitleSaving: __, ...headerHandlers } = params.headerActions

  return {
    setInputValue: params.input.setInputValue,
    setInputMode: params.input.setInputMode,
    handleSend: params.handleSend,
    handleStop: params.handleStop,
    handleRemoveAttachment: params.input.handleRemoveAttachment,
    handleFilesSelected: params.input.handleFilesSelected,
    handleAttachFromChats: params.input.handleAttachFromChats,
    handleTemplateSelect: params.input.handleTemplateSelect,
    handleCreateTemplate: params.input.handleCreateTemplate,
    handleTemplateFillSubmit: params.input.handleTemplateFillSubmit,
    handleTemplateFillCancel: params.input.handleTemplateFillCancel,
    handleToolCallClick: params.splitViewActions.handleToolCallClick,
    handleContentBriefClick: params.splitViewActions.handleContentBriefClick,
    handleMessageClick: params.splitViewActions.handleMessageClick,
    handleReportClick: params.splitViewActions.handleReportClick,
    handleUserInteractionClick: params.splitViewActions.handleUserInteractionClick,
    handleCloseSplitView: params.splitViewActions.handleCloseSplitView,
    handleAttachmentDownload: params.handleAttachmentDownload,
    ...headerHandlers,
  }
}

export const buildTitleEditState = (
  headerActions: UseChatHeaderActionsReturn
): ChatViewTitleEditState => ({
  isTitleEditing: headerActions.isTitleEditing,
  isTitleSaving: headerActions.isTitleSaving,
})
